import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { eventLabel } from '../../utils/labels.js';
import { automationApi } from '../../services/automationApi.js';
import { formatDateTime } from '../../utils/formatters.js';

const SEVERITY_VARIANT = { INFO: 'info', WARNING: 'warning', CRITICAL: 'danger' };

export function AlertsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const runs = useQuery({ queryKey: ['automation', 'runs', 'all'], queryFn: () => automationApi.listRuns({ limit: 200 }) });
  const rows = runs.data?.items || [];
  const unread = runs.data?.meta?.unread ?? 0;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['automation', 'runs'] });
  const markRead = useMutation({ mutationFn: (id) => automationApi.markRunRead(id), onSuccess: invalidate });
  const markAll = useMutation({ mutationFn: () => automationApi.markAllRunsRead(), onSuccess: invalidate });

  const columns = [
    { key: 'severity', label: t('auto.col.severity'), render: (r) => <Badge variant={SEVERITY_VARIANT[r.severity]}>{t(`auto.severity.${r.severity}`)}</Badge> },
    { key: 'message', label: t('auto.col.message'), render: (r) => <span style={{ fontWeight: r.isRead ? 400 : 600 }}>{r.message}</span> },
    { key: 'trigger', label: t('auto.col.trigger'), render: (r) => <span className="text-sm">{eventLabel(t, r.triggerEvent)}</span> },
    { key: 'time', label: t('auto.col.time'), render: (r) => formatDateTime(r.createdAt) },
    { key: 'actions', label: '', align: 'right', render: (r) => (r.isRead ? null : <Button size="sm" variant="ghost" disabled={markRead.isPending} onClick={() => markRead.mutate(r.id)}>{t('auto.action.markRead')}</Button>) },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('auto.alerts.title')}</h1>
          <p className="muted">{t('auto.alerts.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="secondary" disabled={markAll.isPending || unread === 0} onClick={() => markAll.mutate()}>{t('auto.action.markAll')} {unread > 0 ? `(${unread})` : ''}</Button>
        </div>
      </header>

      <Table columns={columns} rows={rows} isLoading={runs.isLoading} emptyMessage={t('auto.alerts.empty')} />
    </section>
  );
}
